import axios, { HttpStatusCode } from "axios";
import Cookies from "universal-cookie";

import { checkSocketConnectionAsync } from "../../api";
import {
  JWT_COOKIE_NAME,
  JWT_OLD_APP_INTERNAL_COOKIE_NAME,
  LANG_DEFAULT,
  LANG_HEADER_NAME,
  RoutesApiConst,
} from "../../constants";
import {
  LoginAuthType,
  passwordAuthTypes,
  ResponseStatusEnum,
} from "../../enums";
import { TPasswordBasedLoginAuthType } from "../../models/auth/auth.models";
import { apiBaseUrl } from "../env.helpers";
import { getLangLocalStorage } from "../rtl.helpers";
import { loginUrlRedirect, normalizeUrlBasePath } from "../url.helpers";
import { localStorageHelpers, ProfileStorage } from "./../browser-storge";
import { Log } from "./../log.helpers";
import { logRefreshTokenExpiration, logTokenExpiration } from "./token-logging";

export const sessionBootstrapFromBearerAccessToken = async (
  accessToken: string,
): Promise<boolean> => {
  try {
    const res = await axios.post(
      `${normalizeUrlBasePath(apiBaseUrl() || "")}${RoutesApiConst.auth.sessionBootstrap}`,
      null,
      {
        withCredentials: true,
        headers: { Authorization: `Bearer ${accessToken}` },
        validateStatus: status =>
          status === HttpStatusCode.NoContent || status === HttpStatusCode.Ok,
      },
    );

    return (
      res.status === HttpStatusCode.NoContent ||
      res.status === HttpStatusCode.Ok
    );
  } catch {
    return false;
  }
};

/** True when `GET session/me` succeeds (cookie session active). */
export const verifyHttpOnlySessionActive = async (): Promise<boolean> => {
  const url = `${normalizeUrlBasePath(apiBaseUrl() || "")}${RoutesApiConst.auth.sessionMe}`;

  const res = await axios.get(url, {
    withCredentials: true,
    headers: {
      Accept: "application/json",
      [LANG_HEADER_NAME]: getLangLocalStorage() || LANG_DEFAULT,
    },
    validateStatus: status =>
      status === HttpStatusCode.Ok || status === HttpStatusCode.Unauthorized,
  });

  return (
    res.status === HttpStatusCode.Ok &&
    res.data?.status === ResponseStatusEnum.Success
  );
};

const hasExistingHttpOnlySession = async (): Promise<boolean> =>
  verifyHttpOnlySessionActive();

/**
 * If a legacy app JWT cookie exists on the SPA host, exchange it for HttpOnly api session cookies.
 */
const migrateLegacyJwtCookieToSession = async (): Promise<boolean> => {
  const cookies = new Cookies();
  const cookieToken = cookies.get(JWT_COOKIE_NAME);
  Log.debug("Check cookies while checking for jwt token", cookies);

  if (!cookieToken) {
    return false;
  }

  Log.debug("Token found in cookies, hybrid mode activated");
  ProfileStorage.isHybridMode = true;

  const ok = await sessionBootstrapFromBearerAccessToken(cookieToken);

  cleanupAccessTokenCookie();

  if (ok) {
    Log.info("Successfully migrated legacy JWT cookie to HttpOnly session");
    return true;
  }

  Log.info("Can't migrate legacy JWT cookie: token not valid");
  loginUrlRedirect();

  return false;
};

export interface IAuthInitializationResult {
  isAuthenticated: boolean;
}

export const initializeAuthTokens =
  async (): Promise<IAuthInitializationResult> => {
    if (await migrateLegacyJwtCookieToSession()) {
      const result: IAuthInitializationResult = { isAuthenticated: true };
      return result;
    }

    ProfileStorage.isHybridMode = false;

    try {
      if (await hasExistingHttpOnlySession()) {
        const result: IAuthInitializationResult = { isAuthenticated: true };
        return result;
      }
    } catch (error) {
      Log.debug("Session probe failed", error);
    }

    const result: IAuthInitializationResult = { isAuthenticated: false };
    return result;
  };

export const getAccessToken = (): string | null => {
  const localToken = localStorageHelpers.getItem<string>("authToken");
  return localToken || null;
};

export const getAuthorizationHeader = ():
  { Authorization: string } | Record<string, never> => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const cleanupAccessTokenCookie = (): void => {
  const cookies = new Cookies();
  cookies.remove(JWT_COOKIE_NAME);
  cookies.remove(JWT_OLD_APP_INTERNAL_COOKIE_NAME);
};

export const clearStorageTokens = (): void => {
  // `returnTo` is a deep-link redirect target, not a token. It is cleared by
  // navigateLogin() on explicit logout and by setOrChooseProfile() once it has
  // been consumed for company auto-select. Wiping it here would also drop the
  // user's intended destination on transient 401s (e.g. during the MS OAuth
  // round-trip), causing the choose-company popup to open after login.
  localStorageHelpers.removeItem("authToken");
  localStorageHelpers.removeItem("refreshToken");
};

export const cleanupAuthStoredTokenData = (): void => {
  clearStorageTokens();
  ProfileStorage.authSet = undefined;
  ProfileStorage.authSetOwnerUserId = undefined;
  cleanupAccessTokenCookie();
};

export const setAccessToken = (token?: string | null): void => {
  if (token) {
    localStorageHelpers.setItem("authToken", token);
  } else {
    localStorageHelpers.removeItem("authToken");
  }
};

export const getRefreshToken = (): string | null => {
  const token = localStorageHelpers.getItem<string>("refreshToken");

  return token;
};

export const setRefreshToken = (token: string | null | undefined): void => {
  if (token) {
    localStorageHelpers.setItem("refreshToken", token);
  } else {
    localStorageHelpers.removeItem("refreshToken");
  }
};

interface IResolveLoginParamsArguments {
  [LoginAuthType.Username]?: string;
  [LoginAuthType.Email]?: string;
  [LoginAuthType.Phone]?: string;
  loginType: LoginAuthType.Username | LoginAuthType.Email | LoginAuthType.Phone;
}

export const resolveLoginTypeParams = ({
  Username,
  Email,
  Phone,
  loginType,
}: IResolveLoginParamsArguments):
  { Username: string } | { Email: string } | { Phone: string } => {
  switch (loginType) {
    case LoginAuthType.Username:
      return { Username: Username as string };
    case LoginAuthType.Email:
      return { Email: Email as string };
    case LoginAuthType.Phone:
      return { Phone: Phone as string };
  }
};

export const checkIsPasswordAuthTypes = (
  loginAuthTypes: LoginAuthType[],
): boolean =>
  loginAuthTypes?.some(loginAuthType => passwordAuthTypes.has(loginAuthType));

export const isPasswordBasedLoginAuthType = (
  loginType: LoginAuthType,
): loginType is TPasswordBasedLoginAuthType => {
  return passwordAuthTypes.has(loginType);
};

export const updateTokensAndReconnectSocket = ({
  accessToken,
  refreshToken,
}: {
  accessToken: string | null | undefined;
  refreshToken?: string | null;
}): void => {
  logTokenExpiration(accessToken, "TOKEN UPDATED");
  logRefreshTokenExpiration(refreshToken);
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);

  if (accessToken) {
    void checkSocketConnectionAsync(accessToken);
  } else {
    void checkSocketConnectionAsync(undefined, { allowCookieAuth: true });
  }
};

export const isLoginPage = (): boolean =>
  window.location.hash?.includes("/login") || false;
